/**
 * Shuffle bag (draw-without-replacement).
 *
 * A bag is identified by a numeric `seed` plus a running `cursor` (how many
 * draws have been made). Each contiguous block of `size` draws is a full
 * Fisher-Yates permutation of [0, size), so an index NEVER reappears before the
 * bag is exhausted (all `size` items drawn). Once a block is used up the next
 * block is a fresh independent permutation, and the cursor keeps advancing, so
 * re-entering a screen never restarts the exact same order.
 *
 * Only { seed, cursor } is persisted — the permutation is recomputed on demand,
 * so saves stay tiny and stay valid even if `size` later changes (a grown pool
 * just widens future permutations; old cursors keep working, no crash).
 */

/** mulberry32 PRNG — deterministic sequence from a numeric seed (self-contained). */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Persisted bag position. `seed` fixes the shuffle, `cursor` how many drawn. */
export interface BagCursor {
  seed: number;
  cursor: number;
}

/** Named collection of bag cursors (one per pool/room/category). */
export type BagStore = Record<string, BagCursor>;

/** A fresh random 32-bit seed (the only place Math.random touches a bag). */
export function newSeed(): number {
  return (Math.random() * 0x1_0000_0000) >>> 0;
}

/** Fisher-Yates permutation of [0, size) for a given seed + block index (pure). */
export function bagBlock(seed: number, block: number, size: number): number[] {
  const rand = mulberry32((seed ^ Math.imul(block + 1, 0x9e3779b1)) >>> 0);
  const perm = Array.from({ length: size }, (_, i) => i);
  for (let i = size - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    const tmp = perm[i];
    perm[i] = perm[j];
    perm[j] = tmp;
  }
  return perm;
}

/** The index an absolute cursor position maps to (pure; no mutation). */
export function bagAt(seed: number, cursor: number, size: number): number {
  if (size <= 1) return 0;
  const block = Math.floor(cursor / size);
  const pos = ((cursor % size) + size) % size;
  return bagBlock(seed, block, size)[pos];
}

/** Draw one index from a named bag; returns the index + the advanced store. */
export function draw(bags: BagStore, key: string, size: number): { index: number; bags: BagStore } {
  const bag = bags[key] ?? { seed: newSeed(), cursor: 0 };
  const index = bagAt(bag.seed, bag.cursor, size);
  return { index, bags: { ...bags, [key]: { seed: bag.seed, cursor: bag.cursor + 1 } } };
}

/** Draw `n` indices in sequence from one bag (distinct within a bag cycle). */
export function drawMany(
  bags: BagStore, key: string, size: number, n: number
): { indices: number[]; bags: BagStore } {
  let store = bags;
  const indices: number[] = [];
  for (let i = 0; i < n; i++) {
    const res = draw(store, key, size);
    indices.push(res.index);
    store = res.bags;
  }
  return { indices, bags: store };
}

/** Migrate a persisted bag store (keeps valid cursors, drops malformed entries). */
export function migrateBags(saved: unknown): BagStore {
  if (!saved || typeof saved !== 'object') return {};
  const out: BagStore = {};
  for (const [k, v] of Object.entries(saved as Record<string, unknown>)) {
    const bag = v as Partial<BagCursor> | null;
    if (bag && typeof bag.seed === 'number' && typeof bag.cursor === 'number') {
      out[k] = { seed: bag.seed, cursor: bag.cursor };
    }
  }
  return out;
}
